import React, {
  useMemo, useRef, useCallback, useState, useEffect,
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import useQlikApp from 'hooks/useQlikApp';
import useQlikSessionObject from 'hooks/useQlikSessionObject';
import data from '../../data';

const useStyles = makeStyles(() => ({
  formControl: {
    width: 144,
    marginLeft: 20,
    '& .MuiFilledInput-root': {
      backgroundColor: (props) => (props.color === 'light' ? '#FFFDED' : '#F6F9E6'),
      border: '1px solid #D4D7C6 !important',
    },
    '& .MuiFilledInput-root::before': {
      border: 'none !important',
    },
    '& .MuiFilledInput-root::after': {
      border: 'none !important',
    },
  },
  selectLabel: {
    color: '#404040 !important',
    top: '-6px !important',
    '&.MuiInputLabel-shrink': {
      visibility: 'hidden',
    },
  },
  select: {
    borderRadius: '3px !important',
    color: '#404040 !important',
    '& > svg': {
      fill: '#404040 !important',
    },
    '& .MuiSelect-select': {
      padding: '10px 25px 10px 10px !important',
    },
  },
  selected: {
    backgroundColor: '#28a745 !important',
    color: '#FFFFFF !important',
  },
  excluded: {
    color: '#d1d1d1 !important',
  },
}));

const Filter = ({
  field, label, color, lang, handleCountryStateChange,
}) => {
  const qlikApp = useQlikApp();

  const { current: id } = useRef(uuidv4());

  const [selectedCountry, setSelectedCountry] = useState('');

  const qFilter = useQlikSessionObject({
    app: qlikApp,
    properties: {
      qListObjectDef: {
        qDef: {
          qFieldDefs: [`[${field}]`],
          qFieldLabels: [label],
        },
        qFrequencyMode: 'V',
      },
    },
  });

  const options = useMemo(() => {
    if (!qFilter.layout) return [];
    return qFilter.layout.qListObject.qDataPages[0].qMatrix.map((row) => ({
      qElemNumber: row[0].qElemNumber,
      qState: row[0].qState,
      qText: row[0].qText,
      qFrequency: row[0].qFrequency,
    }));
  }, [qFilter]);

  const selected = useMemo(() => {
    if (!qFilter.layout && field !== 'YearCity') return [];
    if (!qFilter.layout && field === 'YearCity') return '';
    return qFilter.layout.qListObject.qDataPages[0].qMatrix
      .filter((row) => row[0].qState === 'S')
      .map((row) => row[0].qElemNumber);
  }, [qFilter.layout]);

  const handleChange = useCallback((event) => {
    if (!qFilter.model) return;
    const qValue = event.target.value;
    if (qValue.length === 1) {
      setSelectedCountry(qValue);
    } else {
      handleCountryStateChange('');
    }
    qFilter.model.selectListObjectValues('/qListObjectDef', Array.isArray(qValue) ? qValue : [qValue], false);
  }, [qFilter.model]);

  useEffect(() => {
    if (!qFilter.layout) return;
    const _selectedCountry = options.filter((o) => selectedCountry.includes(o.qElemNumber));
    handleCountryStateChange(_selectedCountry[0].qText);
  }, [selectedCountry]);

  const classes = useStyles({ color });

  return (
    <div>
      <FormControl variant="filled" className={classes.formControl}>
        <InputLabel
          id={`${id}-label`}
          className={classes.selectLabel}
        >
          {lang === 'jp' ? data.jp.filters[`${label}`] : data.en.filters[`${label}`]}
        </InputLabel>
        <Select
          labelId={`${id}-label`}
          id={id}
          multiple={field !== 'YearCity'}
          value={selected}
          onChange={handleChange}
          className={classes.select}
          defaultValue={field === 'YearCity' ? '' : []}
        >
          {options.map((row) => (
            (row.qState !== 'X' && (
            <MenuItem
              key={row.qElemNumber}
              value={row.qElemNumber}
              name={row.qText}
              disabled={row.qState === 'X'}
              classes={{
                root: clsx({
                  [classes.selected]: row.qState === 'S',
                  [classes.excluded]: row.qState === 'X',
                }),
              }}
            >
              {row.qText}
            </MenuItem>
            ))
          ))}
          {options.map((row) => (
            (row.qState === 'X' && (
            <MenuItem
              key={row.qElemNumber}
              value={row.qElemNumber}
              name={row.qText}
              disabled={row.qState === 'X'}
              classes={{
                root: clsx({
                  [classes.selected]: row.qState === 'S',
                  [classes.excluded]: row.qState === 'X',
                }),
              }}
            >
              {row.qText}
            </MenuItem>
            ))
          ))}
        </Select>
      </FormControl>
    </div>
  );
};

Filter.propTypes = {
  field: PropTypes.string.isRequired,
  label: PropTypes.string,
  lang: PropTypes.string,
  color: PropTypes.string,
  handleCountryStateChange: PropTypes.func,
};

Filter.defaultProps = {
  color: '',
  label: '',
  lang: 'en',
  handleCountryStateChange: () => {},
};

export default Filter;
